import { DifferenceModule } from './types';
// ---------------------------------------------------------------------------
// Type lookup tables
// ---------------------------------------------------------------------------
const textDifferenceTypes = new Set([
    'Change',
    'Insertion',
    'Deletion',
    'Space',
    'SpaceHyphen',
    'Case',
    'Hyphen',
    'Bold',
    'Italic',
    'Size',
    'FontSize',
    'Underlined',
    'Subscript',
    'Superscript',
    'FontType',
    'FontColor',
    'SoftHyphen',
    'LineBreakSpace',
    'ReadingOrder',
    'UnmatchedAnnotation',
]);
const spellingDifferenceTypes = new Set(['SpellingGeneral', 'General', 'Abbreviation', 'ProperNoun', 'Alphanumeric']);
const verifyModulesByType = {
    Markup: DifferenceModule.markup,
    Color: DifferenceModule.color,
    FontMetrics: DifferenceModule.fontMetrics,
    Graphics: DifferenceModule.graphics,
    EmbeddedImage: DifferenceModule.text,
    Barcode: DifferenceModule.barcode,
    Braille: DifferenceModule.braille,
};
// UI display names keyed by resolved difference type — used ONLY by the CTF adapter.
// This does NOT set the label shown in the report: the report payload carries only the
// numeric index / groupIndex / subgroupIndex, and CTF renders its own labels downstream.
// The table's sole purpose is to reproduce the displayName the Verify UI shows (apps/api
// difference.builder getDisplayName) so the shared differenceType sort assigns the same
// groupIndex / subgroupIndex on the CTF path as on the UI path. fromVerifyDifference does
// not use this — it already carries the UI displayName from the API.
// Types whose UI label equals the raw type (Change, Markup, …) are omitted and fall back via
// `?? type`. Color is intentionally not matched: its UI label ("Color Point #N") needs a
// per-difference index, but Color is a single-type module so the differenceType sort never runs.
const uiDisplayNamesByType = {
    FontType: 'Font Type',
    FontSize: 'Font Size',
    Size: 'Font Size',
    FontColor: 'Font Color',
    Underlined: 'Underline',
    Subscript: 'Sub/Super',
    Superscript: 'Sub/Super',
    LineBreakSpace: 'Line Break',
    SoftHyphen: 'Soft Hyphen',
    Hyphen: 'Change',
    ReadingOrder: 'Reading Order',
    // All spelling subtypes collapse to 'Spelling' to match the UI card label.
    SpellingGeneral: 'Spelling',
    Abbreviation: 'Spelling',
    ProperNoun: 'Spelling',
    Alphanumeric: 'Spelling',
    EmbeddedImage: 'Image Detected',
    UnmatchedAnnotation: 'Unmatched Annotation',
    FontMetrics: 'Minimum Font Size',
};
// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------
/**
 * Returns true for CTF difference rows that have no card in the UI and no section in reports.
 * Call this to pre-filter before passing rows to fromCtfDifference or computeDifferenceNumbering.
 */
export const isIgnoredCtfDifference = (difference) => difference.algorithm === DifferenceModule.textMatch;
export const fromVerifyDifference = (difference) => {
    const type = difference.type;
    const sourceText = toOptionalString(difference.source?.text);
    const targetText = toOptionalString(difference.target?.text);
    return {
        id: difference.id,
        module: moduleFromVerifyType(type),
        type,
        displayName: difference.displayName,
        location: toNormalizedLocation(difference.source?.location?.pageNumber, difference.source?.location?.rect?.[0], difference.source?.location?.rect?.[1]) ??
            toNormalizedLocation(difference.target?.location?.pageNumber, difference.target?.location?.rect?.[0], difference.target?.location?.rect?.[1]),
        groupId: toOptionalString(difference.groupId),
        word: toOptionalString(difference.word),
        statusId: difference.statusId ?? undefined,
        resyncId: toOptionalNumber(difference.resyncId),
        matchIndex: toOptionalNumber(difference.matchIndex),
        fontPointSize: toOptionalNumber(difference.fontMetricsDetails?.fontPointSize),
        sourceText,
        targetText,
    };
};
export const fromCtfDifference = (difference) => {
    const details = difference.differenceDetails;
    const type = getCtfType(difference);
    const masterText = toOptionalString(details.master?.text);
    const sampleText = toOptionalString(details.sample?.text);
    // Prefer the master side, then sample, then the flat location field. The sample fallback
    // matters for rows that only exist on one document — markups drawn on the new file are stored
    // as { sample: { location } } with no master key, so without it they normalize to no location
    // at all and every one of them ties in the reading-order sort. This mirrors fromVerifyDifference's
    // source-then-target fallback so both adapters order the same rows the same way.
    const location = fromCtfLocation(details.master?.location) ??
        fromCtfLocation(details.sample?.location) ??
        fromCtfLocation(details.location);
    return {
        id: difference.id,
        module: moduleFromCtfDifference(difference),
        type,
        displayName: uiDisplayNamesByType[type] ?? type,
        location,
        groupId: toOptionalString(difference.groupId),
        word: toOptionalString(details.word),
        statusId: difference.statusId ?? undefined,
        resyncId: toOptionalNumber(details.resyncId),
        matchIndex: toOptionalNumber(details.matchIndex),
        fontPointSize: toOptionalNumber(details.fontPointSize),
        sourceText: masterText ?? toOptionalString(details.word) ?? toOptionalString(details.text),
        targetText: sampleText,
    };
};
// ---------------------------------------------------------------------------
// Module resolution helpers
// ---------------------------------------------------------------------------
const moduleFromVerifyType = (type) => {
    const module = verifyModulesByType[type] ??
        (spellingDifferenceTypes.has(type) ? DifferenceModule.spelling : undefined) ??
        (textDifferenceTypes.has(type) ? DifferenceModule.text : undefined);
    if (module)
        return module;
    throw new Error(`Unsupported Verify difference type: ${type}`);
};
const moduleFromCtfDifference = (difference) => {
    const type = getCtfType(difference);
    if (type === 'Markup')
        return DifferenceModule.markup;
    const module = (spellingDifferenceTypes.has(type) ? DifferenceModule.spelling : undefined) ??
        (textDifferenceTypes.has(type) ? DifferenceModule.text : undefined);
    if (module)
        return module;
    return moduleFromCtfAlgorithm(difference.algorithm);
};
const getCtfType = ({ algorithm, differenceDetails }) => {
    if (differenceDetails.differenceType) {
        return differenceDetails.differenceType;
    }
    if (differenceDetails.errorType) {
        return differenceDetails.errorType === 'General' ? 'SpellingGeneral' : differenceDetails.errorType;
    }
    // Legacy spelling rows predate errorType; the API builder maps this case to SpellingGeneral
    // (getTypeName), so mirror it here to keep the normalized type on the UI contract.
    return algorithm === 'Spelling' ? 'SpellingGeneral' : algorithm;
};
/**
 * Maps a CTF algorithm string to a DifferenceModule.
 * Throws on unknown algorithms so contract drift is caught at the call site rather than
 * silently mislabeling a module.
 */
const moduleFromCtfAlgorithm = (algorithm) => {
    if (Object.values(DifferenceModule).includes(algorithm)) {
        return algorithm;
    }
    throw new Error(`Unsupported CTF difference algorithm: ${algorithm}`);
};
// ---------------------------------------------------------------------------
// Location normalization helpers
// ---------------------------------------------------------------------------
/**
 * Normalizes a CTF bounding box the same way the API's difference builder does (calculateLocation),
 * so a difference sorts to the same position whether it reached us as a raw CTF row or as a Verify
 * difference. Bounding boxes on rotated content can have top > bottom (see getRotationIndex), and
 * the builder takes the smaller of the two as the rect's y — using raw `top` here would order
 * rotated pages differently in reports than in the UI.
 */
const fromCtfLocation = (location) => {
    const box = location?.boundingBox;
    const top = toOptionalNumber(box?.top);
    const bottom = toOptionalNumber(box?.bottom);
    const y = top !== undefined && bottom !== undefined ? Math.min(top, bottom) : top;
    return toNormalizedLocation(location?.pageNumber, box?.left, y);
};
const toNormalizedLocation = (pageNumber, x, y) => {
    const p = toOptionalNumber(pageNumber);
    const nx = toOptionalNumber(x);
    const ny = toOptionalNumber(y);
    if (p === undefined || nx === undefined || ny === undefined)
        return undefined;
    // Page numbers are 1-based everywhere in Verify, so page 0 is never a real position. It is the
    // placeholder the API builder emits (generateVerifyDocument / calculateLocation) for a document
    // side that has no coordinates. Treating it as a location would make it win the `??` chains above
    // and suppress the fallback to the side that actually has coordinates.
    if (p < 1)
        return undefined;
    return { pageNumber: p, x: nx, y: ny };
};
// ---------------------------------------------------------------------------
// Primitive coercion helpers
// ---------------------------------------------------------------------------
const toOptionalString = (value) => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    return value;
};
const toOptionalNumber = (value) => {
    return value === null || value === undefined ? undefined : value;
};
//# sourceMappingURL=adapters.js.map